import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { assertWorkspaceInvariant } from '../utils/workspaceInvariant';

export type SlackNotificationChannelTarget = 'boards' | 'threads';

export type UpsertSlackNotificationChannelRequest = {
  workspaceId: string;
  channelId: string;
  label: string;
  enabled: boolean;
  target?: SlackNotificationChannelTarget;
};

export async function upsertSlackNotificationChannel(
  request: UpsertSlackNotificationChannelRequest
): Promise<{ ok: boolean; channelId: string }> {
  assertWorkspaceInvariant(request.workspaceId, { source: 'notifications.upsertSlackNotificationChannel' });
  const callable = httpsCallable(functions, 'upsertSlackNotificationChannelFn');
  const result = await callable(request);
  return result.data as { ok: boolean; channelId: string };
}

export async function deleteNotificationChannel(request: {
  workspaceId: string;
  channelId: string;
  target?: SlackNotificationChannelTarget;
}): Promise<{ ok: boolean }> {
  assertWorkspaceInvariant(request.workspaceId, { source: 'notifications.deleteNotificationChannel' });
  const callable = httpsCallable(functions, 'deleteNotificationChannelFn');
  const result = await callable(request);
  return result.data as { ok: boolean };
}

export async function updateWorkspaceNotificationRules(request: {
  workspaceId: string;
  onCreate?: boolean;
  onUpdate?: boolean;
  threadOnCreate?: boolean;
  threadOnAppend?: boolean;
  threadOnComment?: boolean;
  threadOnStatusChange?: boolean;
  emailNotifyDefault?: boolean;
}): Promise<{ ok: boolean }> {
  assertWorkspaceInvariant(request.workspaceId, { source: 'notifications.updateWorkspaceNotificationRules' });
  const callable = httpsCallable(functions, 'updateWorkspaceNotificationRulesFn');
  const result = await callable(request);
  return result.data as { ok: boolean };
}

export async function sendTestNotification(request: {
  workspaceId: string;
  channelId: string;
  message?: string;
}): Promise<{ ok: boolean }> {
  assertWorkspaceInvariant(request.workspaceId, { source: 'notifications.sendTestNotification' });
  const callable = httpsCallable(functions, 'sendTestNotificationFn');
  const result = await callable(request);
  return result.data as { ok: boolean };
}

export async function registerSlackIncomingWebhook(request: {
  workspaceId: string;
  target: SlackNotificationChannelTarget;
  label?: string;
  webhookUrl: string;
}): Promise<{ ok: boolean; channelId: string; testOk: boolean }> {
  assertWorkspaceInvariant(request.workspaceId, { source: 'notifications.registerSlackIncomingWebhook' });
  const callable = httpsCallable(functions, 'registerSlackIncomingWebhookFn');
  const result = await callable(request);
  return result.data as { ok: boolean; channelId: string; testOk: boolean };
}

export async function startSlackInstall(request: {
  workspaceId: string;
  origin: string;
  target?: SlackNotificationChannelTarget;
}): Promise<{ ok: boolean; authUrl: string; state: string; expiresAtMillis: number }> {
  assertWorkspaceInvariant(request.workspaceId, { source: 'notifications.startSlackInstall' });
  const callable = httpsCallable(functions, 'startSlackInstallFn');
  const result = await callable(request);
  return result.data as { ok: boolean; authUrl: string; state: string; expiresAtMillis: number };
}

export async function enqueueThreadCreateEmailMembersNotification(request: {
  workspaceId: string;
  threadId: string;
  targetUserIds?: string[];
}): Promise<{ ok: boolean; created: boolean; taskId: string }> {
  assertWorkspaceInvariant(request.workspaceId, { source: 'notifications.enqueueThreadCreateEmailMembersNotification' });
  const callable = httpsCallable(functions, 'enqueueThreadCreateEmailMembersNotificationFn');
  const result = await callable(request);
  return result.data as { ok: boolean; created: boolean; taskId: string };
}

export async function enqueueThreadAppendEmailMembersNotification(request: {
  workspaceId: string;
  threadId: string;
  postId: string;
  targetUserIds?: string[];
}): Promise<{ ok: boolean; created: boolean; taskId: string }> {
  assertWorkspaceInvariant(request.workspaceId, { source: 'notifications.enqueueThreadAppendEmailMembersNotification' });
  const callable = httpsCallable(functions, 'enqueueThreadAppendEmailMembersNotificationFn');
  const result = await callable(request);
  return result.data as { ok: boolean; created: boolean; taskId: string };
}

export async function enqueueThreadCommentEmailMembersNotification(request: {
  workspaceId: string;
  threadId: string;
  commentId: string;
  targetUserIds?: string[];
}): Promise<{ ok: boolean; created: boolean; taskId: string }> {
  assertWorkspaceInvariant(request.workspaceId, { source: 'notifications.enqueueThreadCommentEmailMembersNotification' });
  const callable = httpsCallable(functions, 'enqueueThreadCommentEmailMembersNotificationFn');
  const result = await callable(request);
  return result.data as { ok: boolean; created: boolean; taskId: string };
}

